import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc, collection, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { supabase } from "./supabaseClient";
import { FaArrowLeft } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import "./ViewIdeaPage.css";
import { getUserById } from "../utils/searchService";
import { getStage } from "../utils/evolveUtils";
import { getAuthor } from "../utils/authorLookup";

import { useAuth } from "../authContext";
import SaveButton from "../components/SaveButton";

// ✅ NEW: report & block
import ThreeDotsMenu from "../components/ThreeDotsMenu";
import ConfirmModal from "../components/ConfirmModal";
import useBlockedUsers from "../hooks/useBlockedUsers";
import TopMindsBadge from "../TopMindsBadge";

// Import Assets
import SparkImg from "../assets/spark journey page.png";
import OrbitImg from "../assets/orbit journey page.png";
import GravityImg from "../assets/gravity journey page.png";
import NovaImg from "../assets/nova journey page.png";
import ConstellationImg from "../assets/constellation journey page.png";
import EvolveButtonImg from "../assets/evolve - button.png";

/**
 * Resolves a user's profile image.
 * Looks up the user's Firestore doc, grabs whichever avatar field is set,
 * and if it's a Supabase storage path (not already a full URL), signs it.
 * NOTE: "users" requires sign-in in Firestore rules, so for a logged-out
 * visitor this will fail — that's expected, and the try/catch below just
 * falls back to no photo instead of crashing.
 */
const fetchProfileImage = async (userId) => {
  if (!userId) return "";
  try {
    const userDocRef = doc(db, "users", userId);
    const userSnap = await getDoc(userDocRef);
    if (userSnap.exists()) {
      const userData = userSnap.data();
      let avatar = userData.profileImageUrl || userData.photoURL || userData.profileImage || "";

      if (avatar && !avatar.startsWith("http")) {
        const { data: signedUrlData } = await supabase
          .storage
          .from("images")
          .createSignedUrl(avatar, 60 * 60);
        return signedUrlData?.signedUrl || "";
      }
      return avatar;
    }
  } catch (err) {
    console.error("Error fetching profile image:", err);
  }
  return "";
};

const ViewIdeaPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const idea = location.state?.idea;

  const [imageUrl, setImageUrl] = useState("");
  const [authorName, setAuthorName] = useState(idea?.authorName || "");
  const [profileImage, setProfileImage] = useState(idea?.profileImageUrl || null);
  // Top Minds award: starts from the feed's copy if we have it, then confirmed below
  const [authorTopMinds, setAuthorTopMinds] = useState(!!idea?.authorTopMinds);

  // ✅ NEW: holds the idea when the page is opened without navigation state (e.g. from the Inbox)
  const [fetchedIdea, setFetchedIdea] = useState(null);

  // Evolve Data
  const [evolves, setEvolves] = useState([]);
  const [currentStage, setCurrentStage] = useState("Spark");

  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // ✅ NEW: block system
  const { blockedIds, blockUser } = useBlockedUsers();
  const [blockTarget, setBlockTarget] = useState(null);
  const [blocking, setBlocking] = useState(false);
  const [blockError, setBlockError] = useState("");

  // Helper to get stage image
  const getStageImage = (stage) => {
    switch (stage) {
      case "Spark": return SparkImg;
      case "Orbit": return OrbitImg;
      case "Gravity": return GravityImg;
      case "Nova": return NovaImg;
      case "Constellation": return ConstellationImg;
      default: return SparkImg;
    }
  };

  // --- BULLETPROOF SCROLL TO TOP ---
    useEffect(() => {
      // setTimeout pushes the scroll command to the very end of the browser's to-do list,
      // ensuring it happens AFTER the description and images have fully rendered.
      const scrollTimer = setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant'
        });
      }, 50); // A tiny 50ms delay is invisible to the user but fixes the browser glitch

      // Cleanup function in case the user quickly clicks away
      return () => clearTimeout(scrollTimer);
    }, [id]); // This ensures it fires every single time a new idea ID is loaded
    // ---------------------------------

  useEffect(() => {
    const fetchIdeaAndEvolves = async () => {
      let ideaData = idea;

      // Only these two collections are publicly readable per Firestore rules.
      // "competitionIdeas" (raw submissions) and "publicIdeas" are sign-in
      // only, so we only try them when someone is actually logged in —
      // otherwise every anonymous visit logs an avoidable permission error.
      const collectionsToTry = currentUser
        ? ["postedIdeas", "publicIdeas", "competitionIdeas", "PostedCompetitionIdeas"]
        : ["postedIdeas", "PostedCompetitionIdeas"];

      // 1. Fetch Idea if not active
      if (!ideaData) {
        for (const collectionName of collectionsToTry) {
          try {
            const docRef = doc(db, collectionName, id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              ideaData = { id: docSnap.id, ...docSnap.data() };
              break;
            }
          } catch (e) {
            console.error(`Error fetching from ${collectionName}:`, e);
          }
        }
      }

      if (!ideaData) return;

      // ✅ NEW: keep the fetched idea so title / description / tags can render
      if (!idea) setFetchedIdea(ideaData);

      // Update Local State for UI
      if (ideaData.evolveCount) {
        setCurrentStage(getStage(ideaData.evolveCount));
      }

      // Author Logic
      const userId = ideaData.uid || ideaData.userId || ideaData.ownerId || ideaData.authorId;
      if (userId) {
        // .catch() guards against getUserById rejecting (e.g. permission
        // denied for a logged-out visitor) turning into an uncaught promise
        // rejection, which is what was crashing the page.
        getUserById(userId)
          .then(u => {
            if (u && u.authorName) setAuthorName(u.authorName);
          })
          .catch(err => {
            console.warn("Could not load author (may be logged out):", err);
          });

        // Top Minds award: shared cached lookup (usually already cached from the feed)
        getAuthor(userId)
          .then((a) => {
            if (a) setAuthorTopMinds(!!a.topMinds);
          })
          .catch(() => {});

        // ✅ FIX: fetch the author's profile image if we don't already have one
        if (!profileImage) {
          fetchProfileImage(userId).then(url => {
            if (url) setProfileImage(url);
          });
        }
      }

      // Image Logic
      if (ideaData.imagePath && !imageUrl) {
        const { data } = await supabase.storage.from("images").createSignedUrl(ideaData.imagePath, 3600);
        if (data?.signedUrl) setImageUrl(data.signedUrl);
      }

      // 2. Fetch Evolves Subcollection
      // Evolves are sign-in only by design (Firestore rules), so for a
      // logged-out visitor there's nothing to fetch here — skip entirely
      // instead of attempting reads we know will be denied.
      if (!currentUser) {
        setEvolves([]);
        return;
      }

      let validDocRef = null;
      try {
        for (const coll of collectionsToTry) {
          const testRef = doc(db, coll, id);
          const snap = await getDoc(testRef);
          if (snap.exists()) {
            validDocRef = testRef;
            break;
          }
        }

        if (validDocRef) {
          const freshSnap = await getDoc(validDocRef);
          const freshData = freshSnap.exists() ? freshSnap.data() : ideaData;

          if (freshData?.evolveCount) setCurrentStage(getStage(freshData.evolveCount));

          // Query Evolves
          const evolvesRef = collection(db, validDocRef.path, "evolves");
          const q = query(evolvesRef, orderBy("createdAt", "desc"));
          const querySnapshot = await getDocs(q);

          const evolvesList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Show immediately with initials as a placeholder while photos load
          setEvolves(evolvesList);

          // ✅ FIX: fetch each evolver's profile image and enrich the list
          const enrichedEvolves = await Promise.all(
            evolvesList.map(async (ev) => {
              const evUserId = ev.userId || ev.uid;
              const authorProfileImage = await fetchProfileImage(evUserId);
              return { ...ev, authorProfileImage };
            })
          );
          setEvolves(enrichedEvolves);
        }
      } catch (err) {
        console.error("Error fetching details/evolves:", err);
      }
    };

    fetchIdeaAndEvolves();
  }, [id, idea, imageUrl, profileImage, currentUser]);

  // ✅ CHANGED: falls back to the fetched idea when there is no navigation state
  const displayIdea = idea || fetchedIdea || {};

  // ✅ NEW: who owns this idea (used for the menu + block handling)
  const ideaOwnerId =
    displayIdea.uid || displayIdea.userId || displayIdea.ownerId || displayIdea.authorId;

  // ✅ NEW: hide evolves written by blocked users
  const visibleEvolves = evolves.filter(
    (ev) => !blockedIds.includes(ev.userId || ev.uid)
  );

  // ✅ NEW: open / confirm the in-app block popup
  const handleBlock = (uid) => {
    setBlockError("");
    setBlockTarget(uid);
  };

  const confirmBlock = async () => {
    try {
      setBlocking(true);
      const blockedUid = blockTarget;
      await blockUser(blockedUid);
      setBlockTarget(null);
      // If you blocked the author of this idea, leave the page
      if (blockedUid === ideaOwnerId) navigate(-1);
    } catch (e) {
      console.error("Block failed:", e);
      setBlockError("Couldn't block this user. Please try again.");
    } finally {
      setBlocking(false);
    }
  };

  const handleUserClick = (e) => {
    e.stopPropagation();
    const userId = displayIdea.authorId || displayIdea.ownerId || displayIdea.uid || displayIdea.userId;
    if (userId) navigate(`/journey/${userId}`);
  };

  const pageTitle = displayIdea.title
    ? `${displayIdea.title} — Trescend`
    : "Trescend — Where Ideas Pay Off";
  const pageDescription = displayIdea.description
    ? displayIdea.description.slice(0, 160)
    : "See this idea and others shared on Trescend.";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      <div className="view-idea-page">
        <div className="idea-header" style={authorTopMinds ? { flexWrap: 'wrap' } : undefined}>
          <FaArrowLeft className="back" onClick={() => navigate(-1)} />

          <div className="left-section" onClick={handleUserClick} style={{ cursor: 'pointer' }}>
            <div className="profile-wrapper">
              {profileImage ? (
                <img src={profileImage} alt={authorName} className="profile-image" loading="lazy" />
              ) : (
                <span className="avatar">{(authorName || "?")[0]}</span>
              )}
            </div>
            <span className="user-name">{authorName || "Unknown User"}</span>
          </div>

          {/* ✅ NEW: three dots menu for the idea (Report / Block) */}
          <div>
            <ThreeDotsMenu
              type="idea"
              id={id}
              ownerId={ideaOwnerId}
              currentUid={currentUser?.uid}
              onBlock={handleBlock}
            />
          </div>

          {authorTopMinds && (
              <div style={{ flex: '0 0 100%', marginTop: '6px', marginLeft: '52px' }}>
                <TopMindsBadge />
              </div>
            )}
         </div>

          {/*<img
            src={getStageImage(currentStage)}
            alt={currentStage}
            className="header-stage-icon"
            style={{ width: '30px', height: '30px', marginLeft: 'auto', objectFit: 'contain' }}
          />
        </div> */}


        <div className="title-container">
          <h1 className="view-idea-title">
            {displayIdea.title?.split(" ").map((word, index) => (
              <span
                key={index}
                className="word-highlight"
                style={{ "--i": index }}
              >
                {word}
              </span>
            ))}
          </h1>
          <div className="bookmark-btn">
            <SaveButton userId={currentUser?.uid} ideaId={id} />
          </div>
        </div>

        <div className="image-wrapper" style={{ position: 'relative' }}>
          {imageUrl ? (
            <img src={imageUrl} alt="Idea" className="view-idea-image" />
          ) : (
            <div className="image-placeholder">Image loading...</div>
          )}


        <div className="tag-container-view" style={{ marginTop: '15px' }}>
                 {displayIdea.tags?.map((tag, index) => (
                   <span key={index} className="tag">{tag}</span>
                 ))}
             </div>
              </div>

        <div className="evolved-status-container" style={{ display: 'flex', alignItems: 'center', marginTop: '10px', marginBottom: '15px', color: '#fff' }}>
          <img
            src={getStageImage(currentStage)}
            alt={currentStage}
            style={{ width: '24px', height: '24px', marginRight: '8px', marginLeft: '15px', objectFit: 'contain'}}
          />

          <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
            Evolved into {currentStage.toLowerCase()}
          </span>
        </div>



        <p className="View-idea-description">{displayIdea.description}</p>

        {/* ✅ REMOVED: old "Report this idea" button (replaced by the three dots menu in the header) */}

        {/* ✅ MOVED: Evolve button now sits above the evolves-list-section */}
        {displayIdea.type !== 'competition' && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px', marginBottom: '5px' }}>
            <img
              src={EvolveButtonImg}
              alt="Evolve"
              style={{ width: '200px', cursor: 'pointer', transition: 'transform 0.2s' }}
              onClick={() => {
                if (!currentUser) {
                  navigate("/Login_page");
                  return;
                }
                navigate(`/evolve/${id}`, { state: { idea: displayIdea } });
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.05)"}
              onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
            />
          </div>
        )}

       {/* <div className="posted-time" style={{ marginBottom: '20px' }}>
          Posted {displayIdea.createdAt?.toDate?.().toDateString() || "recently"}
        </div> */}

        {currentUser && (
          <div className="evolves-list-section">
            {visibleEvolves.map((evolve) => (
              <div key={evolve.id} className="evolve-card-rich">
                <div className="evolve-header">
                  <div className="evolver-info-left">
                    <div className="evolver-avatar">
                      {evolve.authorProfileImage ? (
                        <img
                          src={evolve.authorProfileImage}
                          alt={evolve.authorName}
                          className="evolver-avatar-img"
                        />
                      ) : (
                        (evolve.authorName || "?")[0]
                      )}
                    </div>
                    <span className="evolver-name">{evolve.authorName}</span>
                  </div>
                  <div className="evolver-info-right" style={{ display: "flex", alignItems: "center", gap: "4px" }}>

                    {/* ✅ NEW: three dots menu for each evolve (Report / Block) */}
                    <ThreeDotsMenu
                      type="evolve"
                      id={`${id}/${evolve.id}`}
                      ownerId={evolve.userId || evolve.uid}
                      currentUid={currentUser?.uid}
                      onBlock={handleBlock}
                    />
                  </div>
                </div>
                <div className="evolved-comment-box">
                  <p className="evolved-comment-text">{evolve.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {!currentUser && (
          <div
            style={{ textAlign: "center", padding: "16px", color: "#ccc", cursor: "pointer" }}
            onClick={() => navigate("/Login_page")}
          >
            Log in to see how this idea evolved
          </div>
        )}
      </div>

      {/* ✅ NEW: in-app block confirmation */}
      <ConfirmModal
        open={!!blockTarget}
        title="Block this user?"
        message="You won't see their ideas or evolves anymore. You can't undo this from here yet."
        confirmText="Block"
        danger
        loading={blocking}
        error={blockError}
        onConfirm={confirmBlock}
        onCancel={() => setBlockTarget(null)}
      />
    </>
  );
};

export default ViewIdeaPage;